import dayjs from "dayjs";

import { countries } from "assets/data";

const ALPHA3_TO_ALPHA2 = {
  ABW: "AW",
  ARE: "AE",
  ARG: "AR",
  AUS: "AU",
  AUT: "AT",
  BEL: "BE",
  BGD: "BD",
  BRA: "BR",
  CAN: "CA",
  CHE: "CH",
  CHL: "CL",
  CHN: "CN",
  COL: "CO",
  CUB: "CU",
  CUW: "CW",
  CZE: "CZ",
  DEU: "DE",
  DNK: "DK",
  DOM: "DO",
  ECU: "EC",
  EGY: "EG",
  ESP: "ES",
  FRA: "FR",
  GBR: "GB",
  GHA: "GH",
  GRC: "GR",
  GUY: "GY",
  HTI: "HT",
  HUN: "HU",
  IND: "IN",
  IRL: "IE",
  ITA: "IT",
  JAM: "JM",
  JPN: "JP",
  KOR: "KR",
  LKA: "LK",
  MAR: "MA",
  MEX: "MX",
  NGA: "NG",
  NLD: "NL",
  NOR: "NO",
  NZL: "NZ",
  PAK: "PK",
  PER: "PE",
  POL: "PL",
  PRT: "PT",
  QAT: "QA",
  ROU: "RO",
  SAU: "SA",
  SGP: "SG",
  SUR: "SR",
  SWE: "SE",
  TTO: "TT",
  TUR: "TR",
  USA: "US",
  VEN: "VE",
  ZAF: "ZA",
};

const countryCodes = new Set(countries.map((country) => country.code));

export function savedTravelerId(traveler) {
  return traveler?._id != null ? String(traveler._id) : "";
}

export function savedTravelerName(traveler) {
  return [traveler?.firstName, traveler?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function savedTravelerInitials(traveler) {
  const first = traveler?.firstName?.trim()?.[0] ?? "";
  const last = traveler?.lastName?.trim()?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || "?";
}

export function maskPassport(number) {
  const value = number?.trim();
  if (!value) {
    return "";
  }
  if (value.length <= 4) {
    return value.toUpperCase();
  }
  return `•••• ${value.slice(-4).toUpperCase()}`;
}

function toDateKey(value) {
  if (!value) {
    return "";
  }
  const match = String(value).match(/^\d{4}-\d{2}-\d{2}/);
  if (match) {
    return match[0];
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

function toDateParts(value) {
  const key = toDateKey(value);
  if (!key) {
    return null;
  }
  const [year, month, day] = key.split("-");
  return { year, month, day };
}

function normalizeGender(gender) {
  if (!gender) {
    return "";
  }
  const value = String(gender).trim().toUpperCase();
  if (value.startsWith("M")) {
    return "Male";
  }
  if (value.startsWith("F")) {
    return "Female";
  }
  return "";
}

function toAlpha2CountryCode(value) {
  if (!value) {
    return "";
  }
  const raw = String(value).trim();
  if (!raw) {
    return "";
  }
  const code = raw.toUpperCase();
  if (code.length === 2 && countryCodes.has(code)) {
    return code;
  }
  if (ALPHA3_TO_ALPHA2[code]) {
    return ALPHA3_TO_ALPHA2[code];
  }
  const byLabel = countries.find(
    (country) => country.label.toUpperCase() === raw.toUpperCase(),
  );
  return byLabel?.code || "";
}

function pickPassport(documents) {
  if (!documents?.length) {
    return undefined;
  }
  return documents.find((doc) => doc.holder) ?? documents[0];
}

function digitsOnly(value) {
  return String(value ?? "").replace(/\D/g, "");
}

function phoneFields(contact) {
  const countryCode = digitsOnly(contact?.countryCode);
  let number = digitsOnly(contact?.phoneNumber);
  if (countryCode && number.startsWith(countryCode)) {
    number = number.slice(countryCode.length);
  }
  if (!countryCode && !number) {
    return { number: "", countryCallingCode: "" };
  }
  return {
    countryCallingCode: countryCode,
    number: `${countryCode}${number}`,
  };
}

export function mapSavedTravelerToForm(traveler) {
  const firstName = traveler?.firstName?.trim() || "";
  const lastName = traveler?.lastName?.trim() || "";
  const dob = toDateParts(traveler?.dateOfBirth);
  const gender = normalizeGender(traveler?.gender);
  const emailAddress = traveler?.email?.trim() || "";
  const phone = phoneFields(traveler?.contact);
  const passport = pickPassport(traveler?.documents);
  const expiry = toDateParts(passport?.expiryDate);
  const passportNumber = passport?.number?.trim().toUpperCase() || "";
  const issuanceCountry = toAlpha2CountryCode(passport?.issuanceCountry);
  const nationality = toAlpha2CountryCode(passport?.nationality);
  const expiryDate = toDateKey(passport?.expiryDate);

  return {
    firstName,
    lastName,
    gender,
    dateOfBirthDay: dob?.day || "",
    dateOfBirthMonth: dob?.month || "",
    dateOfBirthYear: dob?.year || "",
    emailAddress,
    number: phone.number,
    countryCallingCode: phone.countryCallingCode,
    docNumber: passportNumber,
    expDateDay: expiry?.day || "",
    expDateMonth: expiry?.month || "",
    expDateYear: expiry?.year || "",
    documents: [
      {
        number: passportNumber,
        expiryDate,
        issuanceCountry,
        nationality,
        documentType: passport?.documentType || "PASSPORT",
        holder: true,
      },
    ],
    passportExpiry: expiryDate,
  };
}

export function applySavedTravelerToSlot({
  traveler,
  index,
  includeContact,
  setFieldValue,
  setFieldError,
  departureDate,
  expiryMessage,
  expiryAcknowledgedRef,
}) {
  const mapped = mapSavedTravelerToForm(traveler);

  setFieldValue(`firstName${index}`, mapped.firstName);
  setFieldValue(`lastName${index}`, mapped.lastName);
  setFieldValue(`gender${index}`, mapped.gender);
  setFieldValue(`dateOfBirthDay${index}`, mapped.dateOfBirthDay);
  setFieldValue(`dateOfBirthMonth${index}`, mapped.dateOfBirthMonth);
  setFieldValue(`dateOfBirthYear${index}`, mapped.dateOfBirthYear);

  if (includeContact) {
    if (mapped.emailAddress) {
      setFieldValue(`emailAddress${index}`, mapped.emailAddress);
    }
    if (mapped.number) {
      setFieldValue(`number${index}`, mapped.number);
    }
    if (mapped.countryCallingCode) {
      setFieldValue(`countryCallingCode${index}`, mapped.countryCallingCode);
    }
  }

  setFieldValue(`docNumber${index}`, mapped.docNumber);
  setFieldValue(`expDateDay${index}`, mapped.expDateDay);
  setFieldValue(`expDateMonth${index}`, mapped.expDateMonth);
  setFieldValue(`expDateYear${index}`, mapped.expDateYear);
  setFieldValue(`documents${index}`, mapped.documents);

  if (expiryAcknowledgedRef?.current) {
    expiryAcknowledgedRef.current[index] = false;
  }

  if (mapped.passportExpiry && departureDate && expiryMessage) {
    const expiryDate = dayjs(mapped.passportExpiry);
    const sixMonthsAfter = dayjs(departureDate).add(6, "months");
    if (expiryDate.isValid() && expiryDate.isBefore(sixMonthsAfter)) {
      setFieldError(`expDateYear${index}`, expiryMessage);
    }
  }
}
